import React, { useMemo } from 'react'
import Typography from '@material-ui/core/Typography'
import { useSnackbar, OptionsObject, SnackbarProvider } from 'notistack'
import { t } from 'i18n'
import { ApolloError } from '@apollo/client'
import { Sentry } from 'components/error'
import { IconButton } from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'

interface SnackbarProps {
  title?: string
  message?: string
}

type DisambiguatedSnackbarProps = Partial<SnackbarProps> & OptionsObject
type CustomSnackbarProps = DisambiguatedSnackbarProps | string | undefined

const DEFAULT_DURATION = 7 * 1000
export const LONG_DURATION = 15 * 1000

const SnackbarCloseButton: React.FC<{ snackbarKey: string }> = ({ snackbarKey }) => {
  const { closeSnackbar } = useSnackbar()

  return (
    <IconButton onClick={() => closeSnackbar(snackbarKey)} color='inherit'>
      <ClearIcon />
    </IconButton>
  )
}

const disambiguateParams = (params: CustomSnackbarProps): DisambiguatedSnackbarProps => {
  if (typeof params === 'object') {
    return params
  } else {
    return { title: params }
  }
}

const Snackbar: React.FC<SnackbarProps> = ({ title, message }) => (
  <div>
    <Typography variant='h5'>{title}</Typography>
    {message !== undefined && <Typography variant='subtitle2'>{message}</Typography>}
  </div>
)

export const NotifyProvider: React.FC = ({ children }) => {
  return (
    <SnackbarProvider autoHideDuration={DEFAULT_DURATION} action={SnackbarCloseButton}>
      {children}
    </SnackbarProvider>
  )
}

export const useNotify = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  return useMemo(() => {
    const notify = ({ title, message, ...other }: DisambiguatedSnackbarProps) => {
      return enqueueSnackbar(<Snackbar title={title} message={message} />, other)
    }

    const notifyError = (params: CustomSnackbarProps = undefined) => {
      const { title, message, ...other } = disambiguateParams(params)

      return notify({ title: title ?? t('error_occurred'), message, variant: 'error', autoHideDuration: LONG_DURATION, ...other })
    }

    const notifyGraphQLError = (error: ApolloError) => {
      Sentry.captureException(error)

      const firstError = error.graphQLErrors?.[0]

      const errorCode = firstError?.message

      if (!errorCode) {
        return
      }

      const { fullMessages, singleMessage } = firstError.extensions || {}

      const message = Array.isArray(fullMessages) && fullMessages.length > 0 ? fullMessages[0] : singleMessage

      return notify({ title: t(`error.${errorCode}`), message, variant: 'error', autoHideDuration: LONG_DURATION })
    }

    const notifySuccess = (params: CustomSnackbarProps = undefined) => {
      const { title, message, ...other } = disambiguateParams(params)

      return notify({ title: title ?? t('notification.success'), message, variant: 'success', ...other })
    }

    return { notify, notifyError, notifyGraphQLError, notifySuccess, closeSnackbar }
  }, [enqueueSnackbar, closeSnackbar])
}
